import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Loader2,
  Monitor,
  Shield,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday', short: 'Mon' },
  { value: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { value: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { value: 'thursday', label: 'Thursday', short: 'Thu' },
  { value: 'friday', label: 'Friday', short: 'Fri' },
  { value: 'saturday', label: 'Saturday', short: 'Sat' },
  { value: 'sunday', label: 'Sunday', short: 'Sun' },
];

const LOGIN_DEVICES = [
  { value: 'desktop', label: 'Desktop', hint: 'Tauri app' },
  { value: 'mobile', label: 'Mobile', hint: 'Phone app' },
  { value: 'web', label: 'Web', hint: 'Browser' },
];

const STEPS = [
  { id: 'account', title: 'Account', short: 'Account', icon: User },
  { id: 'schedule', title: 'Schedule', short: 'Schedule', icon: Clock },
  { id: 'access', title: 'Access & role', short: 'Access', icon: Shield },
  { id: 'monitoring', title: 'Monitoring', short: 'Monitor', icon: Camera },
];

function DayChips({ value = [], onValueChange, testId }) {
  const toggle = (day) => {
    if (value.includes(day)) onValueChange(value.filter((d) => d !== day));
    else onValueChange([...value, day]);
  };

  return (
    <div className="space-y-3.5" data-testid={testId}>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2.5">
        {DAYS_OF_WEEK.map((day) => {
          const on = value.includes(day.value);
          return (
            <button
              key={day.value}
              type="button"
              onClick={() => toggle(day.value)}
              className={cn(
                'rounded-xl border px-1 py-3 text-center text-sm font-medium transition-colors',
                on
                  ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm shadow-indigo-600/25'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300'
              )}
            >
              {day.short}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() =>
            onValueChange(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])
          }
        >
          Weekdays
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => onValueChange(DAYS_OF_WEEK.map((d) => d.value))}
        >
          All days
        </Button>
      </div>
    </div>
  );
}

function DeviceChips({ value = [], onValueChange, testId }) {
  const toggle = (device) => {
    if (value.includes(device)) {
      if (value.length === 1) return;
      onValueChange(value.filter((d) => d !== device));
    } else {
      onValueChange([...value, device]);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={testId}>
      {LOGIN_DEVICES.map((device) => {
        const on = value.includes(device.value);
        return (
          <button
            key={device.value}
            type="button"
            onClick={() => toggle(device.value)}
            className={cn(
              'flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors',
              on
                ? 'border-indigo-600 bg-indigo-50/80 ring-1 ring-indigo-600'
                : 'border-slate-200 hover:border-indigo-300'
            )}
          >
            <div
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                on ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
              )}
            >
              {on && <Check className="h-3 w-3 text-white" />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">{device.label}</p>
              <p className="text-xs text-gray-500">{device.hint}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function StepRail({ step, onJump }) {
  return (
    <div className="border-b border-indigo-100/80 bg-gradient-to-b from-indigo-50/50 to-transparent px-6 pb-5 pt-2 sm:px-8">
      {/* Mobile: progress + label */}
      <div className="sm:hidden space-y-3">
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Step {step + 1} of {STEPS.length}
          </span>
          <span className="font-semibold text-indigo-800">{STEPS[step].title}</span>
        </div>
        <div className="flex gap-2">
          {STEPS.map((_, i) => (
            <div
              key={STEPS[i].id}
              className={cn(
                'h-2 flex-1 rounded-full transition-colors',
                i <= step ? 'bg-indigo-600' : 'bg-slate-200'
              )}
            />
          ))}
        </div>
      </div>

      {/* Desktop: clickable steps */}
      <ol className="hidden sm:grid grid-cols-4 gap-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onJump(i)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
                  active
                    ? 'border-indigo-600 bg-indigo-600 text-white shadow-md shadow-indigo-600/25'
                    : done
                      ? 'border-indigo-200 bg-white text-indigo-900 hover:border-indigo-400'
                      : 'border-slate-200 bg-white text-slate-400'
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold',
                    active
                      ? 'bg-white/20'
                      : done
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'bg-slate-50'
                  )}
                >
                  {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </span>
                <span className="min-w-0 truncate text-sm font-medium">{s.short}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Multi-step staff create/edit form. Shared field set; create requires email+password.
 */
export default function StaffFormWizard({
  open,
  mode = 'create',
  formData,
  setFormData,
  formError,
  onSubmit,
  saving = false,
  teams = [],
  usagePolicies = [],
  telegramEnabled = false,
  TimezoneField,
  companyTimezone,
  t = (k) => k,
  submitTestId,
}) {
  const isCreate = mode === 'create';
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState('');

  useEffect(() => {
    if (open) {
      setStep(0);
      setStepError('');
    }
  }, [open]);

  const update = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const validateStep = (i, data = formData) => {
    if (i === 0) {
      if (!data.name?.trim()) return 'Full name is required';
      if (isCreate) {
        if (!data.email?.trim()) return 'Email is required';
        if (!data.password || data.password.length < 6) {
          return 'Password must be at least 6 characters';
        }
      }
      // Edit: ignore short/accidental password (browser autofill) — leave blank keeps current password
    }
    if (i === 1) {
      if (!data.working_days?.length) return 'Select at least one working day';
      if (!data.shift_start || !data.shift_end) return 'Shift times are required';
    }
    if (i === 2) {
      if (!data.allowed_login_devices?.length) {
        return 'Select at least one login device';
      }
    }
    if (i === 3 && data.allow_screenshot) {
      if (!data.screenshot_interval || data.screenshot_interval < 1) {
        return 'Screenshot interval must be at least 1 minute';
      }
    }
    return '';
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError('');
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setStepError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  const jumpTo = (i) => {
    if (i <= step) {
      setStepError('');
      setStep(i);
      return;
    }
    for (let s = step; s < i; s += 1) {
      const err = validateStep(s);
      if (err) {
        setStep(s);
        setStepError(err);
        return;
      }
    }
    setStepError('');
    setStep(i);
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    if (saving) return;

    if (step < STEPS.length - 1) {
      goNext();
      return;
    }

    // Edit: strip short password autofill so final Update is not blocked
    let dataForValidation = formData;
    if (!isCreate && formData.password && formData.password.length < 6) {
      dataForValidation = { ...formData, password: '' };
      setFormData((prev) =>
        prev.password && prev.password.length < 6 ? { ...prev, password: '' } : prev
      );
    }

    for (let s = 0; s < STEPS.length; s += 1) {
      const err = validateStep(s, dataForValidation);
      if (err) {
        setStep(s);
        setStepError(err);
        return;
      }
    }
    setStepError('');
    onSubmit(e);
  };

  const tid = (name) => `${isCreate ? 'staff' : 'edit-staff'}-${name}`;

  return (
    <div className="flex min-h-0 max-h-[90vh] flex-1 flex-col overflow-hidden rounded-2xl">
      <div className="shrink-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-5 text-white sm:px-8">
        <DialogHeader className="space-y-1.5 text-left">
          <DialogTitle className="text-2xl font-bold tracking-tight text-white">
            {isCreate ? 'Add New Staff Member' : 'Edit Staff Member'}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-indigo-50/90">
            {isCreate
              ? 'A few quick steps to create their account'
              : 'Update details — switch steps anytime'}
          </DialogDescription>
        </DialogHeader>
      </div>

      <div className="shrink-0">
        <StepRail step={step} onJump={jumpTo} />
      </div>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-[340px] flex-1 overflow-y-auto px-6 py-6 sm:min-h-[380px] sm:px-8 sm:py-7">
          {/* Step 1 — Account */}
          {step === 0 && (
            <div className="space-y-6 animate-in fade-in-0 duration-200">
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3.5 text-sm leading-relaxed text-indigo-900/80">
                Who is this person and how will they sign in?
              </div>
              <div className="space-y-2.5">
                <Label htmlFor={tid('name')}>Full name *</Label>
                <Input
                  id={tid('name')}
                  className="h-11"
                  value={formData.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Jane Smith"
                  required
                  data-testid={tid('name-input')}
                />
              </div>
              {isCreate ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2.5">
                    <Label htmlFor={tid('email')}>Email *</Label>
                    <Input
                      id={tid('email')}
                      className="h-11"
                      type="email"
                      value={formData.email}
                      onChange={(e) => update({ email: e.target.value })}
                      placeholder="jane@company.com"
                      required
                      data-testid={tid('email-input')}
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor={tid('password')}>Password *</Label>
                    <Input
                      id={tid('password')}
                      className="h-11"
                      type="password"
                      value={formData.password}
                      onChange={(e) => update({ password: e.target.value })}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      data-testid={tid('password-input')}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <Label htmlFor={tid('password')}>New password</Label>
                  <Input
                    id={tid('password')}
                    className="h-11"
                    type="password"
                    value={formData.password}
                    onChange={(e) => update({ password: e.target.value })}
                    placeholder="Leave blank to keep current"
                    autoComplete="new-password"
                    data-testid={tid('password-input')}
                  />
                  {formData.email && (
                    <p className="text-sm text-gray-500">Login email: {formData.email}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2.5">
                  <Label htmlFor={tid('department')}>{t('staff.department') || 'Department'}</Label>
                  <Input
                    id={tid('department')}
                    className="h-11"
                    value={formData.department}
                    onChange={(e) => update({ department: e.target.value })}
                    placeholder="Engineering"
                    data-testid={tid('department-input')}
                  />
                </div>
                <div className="space-y-2.5">
                  <Label>{t('common.status') || 'Status'}</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => update({ status: value })}
                  >
                    <SelectTrigger className="h-11" data-testid={tid('status-select')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t('common.active') || 'Active'}</SelectItem>
                      <SelectItem value="inactive">{t('common.inactive') || 'Inactive'}</SelectItem>
                      <SelectItem value="fired">Fired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Schedule */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in-0 duration-200">
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3.5 text-sm leading-relaxed text-indigo-900/80">
                Working days, timezone, and shift window.
              </div>
              <div className="space-y-2.5">
                <Label>Working days *</Label>
                <DayChips
                  value={formData.working_days}
                  onValueChange={(days) => update({ working_days: days })}
                  testId={tid('working-days-select')}
                />
              </div>
              <div className="space-y-2.5">
                <Label>Timezone</Label>
                {TimezoneField ? (
                  <TimezoneField
                    value={formData.timezone}
                    onValueChange={(value) => update({ timezone: value })}
                    testId={tid('timezone-select')}
                  />
                ) : null}
                {companyTimezone &&
                  formData.timezone === 'UTC' &&
                  companyTimezone !== 'UTC' && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Company timezone is <strong>{companyTimezone}</strong>. Saving{' '}
                      <strong>UTC</strong> is only correct for staff who truly work on UTC
                      clocks — otherwise late/present will be wrong.
                    </p>
                  )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2.5">
                  <Label htmlFor={tid('shift-start')}>Shift start</Label>
                  <Input
                    id={tid('shift-start')}
                    className="h-11"
                    type="time"
                    value={formData.shift_start}
                    onChange={(e) => update({ shift_start: e.target.value })}
                    data-testid={tid('shift-start-input')}
                  />
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor={tid('shift-end')}>Shift end</Label>
                  <Input
                    id={tid('shift-end')}
                    className="h-11"
                    type="time"
                    value={formData.shift_end}
                    onChange={(e) => update({ shift_end: e.target.value })}
                    data-testid={tid('shift-end-input')}
                  />
                </div>
              </div>
              <div className="space-y-2.5">
                <Label htmlFor={tid('grace')}>Grace period (minutes)</Label>
                <Input
                  id={tid('grace')}
                  className="h-11 max-w-xs"
                  type="number"
                  min="0"
                  value={formData.grace_period}
                  onChange={(e) => update({ grace_period: parseInt(e.target.value, 10) || 0 })}
                  data-testid={isCreate ? 'staff-grace-period-input' : 'edit-staff-grace-input'}
                />
                <p className="text-sm text-gray-500">Late check-in allowance before marking late.</p>
              </div>
            </div>
          )}

          {/* Step 3 — Access */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in-0 duration-200">
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3.5 text-sm leading-relaxed text-indigo-900/80">
                Where they can log in, and their role on the team.
              </div>
              <div className="space-y-2.5">
                <Label className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-gray-500" />
                  Allow login *
                </Label>
                <DeviceChips
                  value={formData.allowed_login_devices}
                  onValueChange={(devices) => update({ allowed_login_devices: devices })}
                  testId={tid('allowed-login-devices-select')}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2.5">
                  <Label>Staff type</Label>
                  <Select
                    value={formData.staff_type}
                    onValueChange={(value) =>
                      update({
                        staff_type: value,
                        team_ids: value === 'manager' ? [] : formData.team_ids,
                      })
                    }
                  >
                    <SelectTrigger className="h-11" data-testid={tid('type-select')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {telegramEnabled && (
                  <div className="space-y-2.5">
                    <Label>Telegram alert</Label>
                    <Select
                      value={formData.telegram_alert ? 'yes' : 'no'}
                      onValueChange={(v) => update({ telegram_alert: v === 'yes' })}
                    >
                      <SelectTrigger className="h-11" data-testid={isCreate ? 'telegram-alert-select' : 'edit-telegram-alert-select'}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {formData.staff_type === 'staff' && (
                <div className="space-y-2.5">
                  <Label>Teams</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="h-11 w-full justify-between font-normal"
                        data-testid={isCreate ? 'team-select-trigger' : 'edit-team-select-trigger'}
                      >
                        <span className="truncate">
                          {formData.team_ids?.length > 0
                            ? `${formData.team_ids.length} team(s) selected`
                            : 'Select teams...'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[min(320px,calc(100vw-2rem))] p-2" align="start">
                      {teams.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500">No teams yet. Create teams first.</p>
                      ) : (
                        <div className="max-h-52 space-y-1 overflow-y-auto">
                          {teams.map((team) => {
                            const on = formData.team_ids?.includes(team.team_id);
                            return (
                              <button
                                key={team.team_id}
                                type="button"
                                className={cn(
                                  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors',
                                  on ? 'bg-indigo-100 text-indigo-900' : 'hover:bg-slate-100'
                                )}
                                onClick={() => {
                                  const next = on
                                    ? formData.team_ids.filter((id) => id !== team.team_id)
                                    : [...(formData.team_ids || []), team.team_id];
                                  update({ team_ids: next });
                                }}
                              >
                                <span
                                  className={cn(
                                    'flex h-5 w-5 items-center justify-center rounded border',
                                    on ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                                  )}
                                >
                                  {on && <Check className="h-3 w-3 text-white" />}
                                </span>
                                <span className="font-medium">{team.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          )}

          {/* Step 4 — Monitoring */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in-0 duration-200">
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3.5 text-sm leading-relaxed text-indigo-900/80">
                Screenshots, force break, and usage policy.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2.5">
                  <Label>Screenshot capture</Label>
                  <Select
                    value={formData.allow_screenshot ? 'yes' : 'no'}
                    onValueChange={(value) =>
                      update({
                        allow_screenshot: value === 'yes',
                        screenshot_interval:
                          value === 'yes' ? formData.screenshot_interval || 5 : 5,
                      })
                    }
                  >
                    <SelectTrigger className="h-11" data-testid={tid('allow-screenshot-select')}>
                      <Camera className="mr-2 h-4 w-4 text-gray-500" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.allow_screenshot && (
                  <div className="space-y-2.5">
                    <Label htmlFor={tid('screenshot-interval')}>Interval (minutes) *</Label>
                    <Input
                      id={tid('screenshot-interval')}
                      className="h-11"
                      type="number"
                      min="1"
                      value={formData.screenshot_interval}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10) || 1;
                        update({ screenshot_interval: Math.max(1, val) });
                      }}
                      required
                      data-testid={
                        isCreate ? 'staff-screenshot-interval' : 'edit-staff-screenshot-interval'
                      }
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2.5">
                <Label htmlFor={tid('force-break')}>Force break time (minutes)</Label>
                <Input
                  id={tid('force-break')}
                  className="h-11 max-w-xs"
                  type="number"
                  min="0"
                  value={formData.force_break_time}
                  onChange={(e) =>
                    update({ force_break_time: parseInt(e.target.value, 10) || 0 })
                  }
                  data-testid={isCreate ? 'staff-force-break-input' : 'edit-staff-force-break-input'}
                />
                <p className="text-sm text-gray-500">0 = disabled. Auto force-break after this idle time.</p>
              </div>
              <div className="space-y-2.5">
                <Label>Usage policy</Label>
                <Select
                  value={formData.group_policy_id || 'none'}
                  onValueChange={(value) =>
                    update({ group_policy_id: value === 'none' ? '' : value })
                  }
                >
                  <SelectTrigger className="h-11" data-testid={tid('usage-policy-select')}>
                    <SelectValue placeholder="No Policy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Policy</SelectItem>
                    {usagePolicies.map((policy) => (
                      <SelectItem key={policy.policy_id} value={policy.policy_id}>
                        {policy.category_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Track allowed / blocked apps and sites.</p>
              </div>
            </div>
          )}

          {(stepError || formError) && (
            <div
              className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              data-testid={isCreate ? 'form-error' : 'edit-form-error'}
            >
              {stepError || formError}
            </div>
          )}
        </div>

        <DialogFooter className="mt-auto flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50/90 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:space-x-0">
          {(stepError || formError) && (
            <p className="w-full text-sm text-red-600 sm:order-first sm:mr-auto sm:w-auto">
              {stepError || formError}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full rounded-2xl border-slate-200 sm:w-auto"
            onClick={goBack}
            disabled={step === 0 || saving}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <div className="flex w-full flex-col-reverse gap-2.5 sm:w-auto sm:flex-row">
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                className="h-11 w-full rounded-2xl bg-indigo-600 shadow-md shadow-indigo-600/25 hover:bg-indigo-700 sm:min-w-[140px] sm:w-auto"
                onClick={goNext}
                disabled={saving}
              >
                Continue
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                className="h-11 w-full rounded-2xl bg-indigo-600 shadow-md shadow-indigo-600/25 hover:bg-indigo-700 sm:min-w-[140px] sm:w-auto"
                data-testid={submitTestId}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isCreate ? 'Creating…' : 'Updating…'}
                  </>
                ) : (
                  isCreate ? 'Create staff' : 'Update staff'
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </form>
    </div>
  );
}
